import React, { useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { 
  Bold, Italic, List, ListOrdered, Heading1, Heading2, Heading3, 
  Minus, Undo, Redo, Quote, Link2, Image as ImageIcon, Code, Eye
} from 'lucide-react';
import { Button } from './ui/button';

const ToolbarButton = ({ icon: Icon, label, onClick, active, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    disabled={disabled}
    className={`p-1.5 rounded transition-colors ${
      disabled
        ? 'text-slate-300 cursor-not-allowed'
        : active
          ? 'bg-slate-700 text-white'
          : 'text-slate-600 hover:bg-slate-200'
    }`}
  >
    <Icon className="h-4 w-4" />
  </button>
);

const Divider = () => <div className="w-px h-5 bg-slate-300 mx-1" />;

const BlogRichTextEditor = ({
  value,
  onChange,
  placeholder = 'Write your blog post content...',
  className = '',
  mode = 'full', // 'full' for blog, 'compact' for community posts (no headings, no inline images)
  'data-testid': testId,
}) => {
  const [viewMode, setViewMode] = React.useState('editor'); // 'editor' or 'preview'

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline hover:text-blue-800',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg my-4',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    onCreate: ({ editor }) => {
      if (value && editor.isEmpty) {
        editor.commands.setContent(value, false);
      }
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const isEmpty = editor.isEmpty;
      onChange(isEmpty ? '' : html);
    },
    editorProps: {
      attributes: {
        'data-testid': testId || 'blog-rich-text-editor',
        class: 'outline-none min-h-[400px] px-4 py-3 prose prose-slate max-w-none',
      },
    },
  });

  // Sync external value changes
  useEffect(() => {
    if (editor && value !== editor.getHTML() && value !== undefined) {
      const { from, to } = editor.state.selection;
      editor.commands.setContent(value || '', false);
      // Try to restore cursor position
      try {
        editor.commands.setTextSelection({ from, to });
      } catch (e) {
        // Ignore if position is invalid
      }
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl || 'https://');
    
    if (url === null) return; // Cancelled
    
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    
    const url = window.prompt('Enter image URL:');
    
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div
      className={`border rounded-lg overflow-hidden bg-white ${className}`}
      data-testid={testId ? `${testId}-wrapper` : 'blog-rich-text-editor-wrapper'}
    >
      {/* Toolbar */}
      <div
        className="flex items-center gap-0.5 px-3 py-2 bg-slate-50 border-b flex-wrap"
        data-testid="blog-rich-text-toolbar"
      >
        {/* View Mode Toggle */}
        <div className="flex items-center gap-1 mr-3 pr-3 border-r border-slate-300">
          <Button
            type="button"
            variant={viewMode === 'editor' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('editor')}
            className="h-7 px-2 text-xs"
            data-testid="blog-editor-mode"
          >
            Edit
          </Button>
          <Button
            type="button"
            variant={viewMode === 'preview' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('preview')}
            className="h-7 px-2 text-xs"
            data-testid="blog-preview-mode"
          >
            <Eye className="h-3 w-3 mr-1" />
            Preview
          </Button>
        </div>

        {/* Formatting Buttons - disabled in preview mode */}
        {mode !== 'compact' && (
          <>
            <ToolbarButton
              icon={Heading1}
              label="Heading 1"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              active={editor.isActive('heading', { level: 1 })}
              disabled={viewMode === 'preview'}
            />
            <ToolbarButton
              icon={Heading2}
              label="Heading 2"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              active={editor.isActive('heading', { level: 2 })}
              disabled={viewMode === 'preview'}
            />
            <ToolbarButton
              icon={Heading3}
              label="Heading 3"
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              active={editor.isActive('heading', { level: 3 })}
              disabled={viewMode === 'preview'}
            />
            <Divider />
          </>
        )}
        <ToolbarButton
          icon={Bold}
          label="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          disabled={viewMode === 'preview'}
        />
        <ToolbarButton
          icon={Italic}
          label="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          disabled={viewMode === 'preview'}
        />
        <ToolbarButton
          icon={Code}
          label="Code"
          onClick={() => editor.chain().focus().toggleCode().run()}
          active={editor.isActive('code')}
          disabled={viewMode === 'preview'}
        />
        <Divider />
        <ToolbarButton
          icon={List}
          label="Bullet list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          disabled={viewMode === 'preview'}
        />
        <ToolbarButton
          icon={ListOrdered}
          label="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          disabled={viewMode === 'preview'}
        />
        <ToolbarButton
          icon={Quote}
          label="Blockquote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          disabled={viewMode === 'preview'}
        />
        <Divider />
        <ToolbarButton
          icon={Link2}
          label="Link"
          onClick={setLink}
          active={editor.isActive('link')}
          disabled={viewMode === 'preview'}
        />
        {mode !== 'compact' && (
          <ToolbarButton
            icon={ImageIcon}
            label="Image"
            onClick={addImage}
            disabled={viewMode === 'preview'}
          />
        )}
        <Divider />
        <ToolbarButton
          icon={Minus}
          label="Horizontal Rule"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          disabled={viewMode === 'preview'}
        />
        <Divider />
        <ToolbarButton
          icon={Undo}
          label="Undo"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={viewMode === 'preview' || !editor.can().undo()}
        />
        <ToolbarButton
          icon={Redo}
          label="Redo"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={viewMode === 'preview' || !editor.can().redo()}
        />
      </div>

      {/* Editor or Preview */}
      {viewMode === 'editor' ? (
        <EditorContent editor={editor} />
      ) : (
        <div 
          className="min-h-[400px] px-4 py-3 prose prose-slate max-w-none blog-preview"
          dangerouslySetInnerHTML={{ 
            __html: value 
              ? value
                  // Replace template tags with visual placeholders for preview
                  .replace(/\{listing:([^}]+)\}/g, '<span class="template-tag">[Listing: $1]</span>')
                  .replace(/\{listings-grid:([^}]+)\}/g, '<span class="template-tag">[Listings Grid: $1]</span>')
                  .replace(/\{featured-listings:([^}]+)\}/g, '<span class="template-tag">[Featured Listings: $1]</span>')
                  .replace(/\{enhanced-listings:([^}]+)\}/g, '<span class="template-tag">[Enhanced Listings: $1]</span>')
                  .replace(/\{recent-listings:([^}]+)\}/g, '<span class="template-tag">[Recent Listings: $1]</span>')
                  .replace(/\{town-link:([^}]+)\}/g, '<span class="template-tag">[Town: $1]</span>')
                  .replace(/\{category-link:([^}]+)\}/g, '<span class="template-tag">[Category: $1]</span>')
                  .replace(/\{cta-button:([^:]+):([^}]+)\}/g, '<span class="template-tag">[Button: $1]</span>')
              : '<p class="text-slate-400">No content yet. Switch to Edit mode to start writing.</p>'
          }}
        />
      )}

      {/* TipTap editor styles */}
      <style>{`
        .tiptap {
          min-height: 400px;
          padding: 0.75rem 1rem;
        }
        .tiptap:focus {
          outline: none;
        }
        .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #94a3b8;
          pointer-events: none;
          height: 0;
        }
        .tiptap h1 {
          font-size: 2rem;
          font-weight: 700;
          margin: 1.5rem 0 1rem;
          color: #0f172a;
          line-height: 1.2;
        }
        .tiptap h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 1.25rem 0 0.75rem;
          color: #1e293b;
          line-height: 1.3;
        }
        .tiptap h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 1rem 0 0.5rem;
          color: #334155;
          line-height: 1.4;
        }
        .tiptap p {
          margin-bottom: 0.75em;
          line-height: 1.7;
        }
        .tiptap ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin-bottom: 0.75em;
        }
        .tiptap ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin-bottom: 0.75em;
        }
        .tiptap li {
          margin-bottom: 0.25em;
        }
        .tiptap blockquote {
          border-left: 4px solid #0F2C3E;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #475569;
          font-style: italic;
          background: #f8fafc;
          padding: 1rem;
          border-radius: 0 0.5rem 0.5rem 0;
        }
        .tiptap hr {
          border: none;
          border-top: 2px solid #e2e8f0;
          margin: 1.5rem 0;
        }
        .tiptap strong {
          font-weight: 600;
        }
        .tiptap em {
          font-style: italic;
        }
        .tiptap code {
          background: #f1f5f9;
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-family: monospace;
          font-size: 0.875em;
          color: #be185d;
        }
        .tiptap a {
          color: #2563eb;
          text-decoration: underline;
          cursor: pointer;
        }
        .tiptap a:hover {
          color: #1d4ed8;
        }
        .tiptap img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
        .blog-preview h1 {
          font-size: 2rem;
          font-weight: 700;
          margin: 1.5rem 0 1rem;
          color: #0f172a;
        }
        .blog-preview h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 1.25rem 0 0.75rem;
          color: #1e293b;
        }
        .blog-preview h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 1rem 0 0.5rem;
          color: #334155;
        }
        .blog-preview p {
          margin: 0.75rem 0;
          line-height: 1.7;
        }
        .blog-preview ul, .blog-preview ol {
          margin: 0.75rem 0;
          padding-left: 1.5rem;
        }
        .blog-preview li {
          margin: 0.25rem 0;
        }
        .blog-preview blockquote {
          border-left: 4px solid #0F2C3E;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #475569;
          font-style: italic;
          background: #f8fafc;
          padding: 1rem;
          border-radius: 0 0.5rem 0.5rem 0;
        }
        .blog-preview a {
          color: #2563eb;
          text-decoration: underline;
        }
        .blog-preview img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
        .blog-preview code {
          background: #f1f5f9;
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-family: monospace;
          font-size: 0.875em;
        }
        .template-tag {
          display: inline-block;
          background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
          color: #3730a3;
          padding: 0.125rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
          font-weight: 500;
          border: 1px solid #a5b4fc;
          margin: 0.125rem;
        }
      `}</style>
    </div>
  );
};

export default BlogRichTextEditor;
